import {showNotification} from 'platform/components';
import {Link, Show} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useLazyGetEmployeeLinkQuery} from '@dms/api/customer';
import {UserAvailabilitySummaryAbsenceResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {employeeRoutes} from '@dms/routes';

import {composePath, useNavigate} from 'shared/web';

import {getUserAbsenceReturnText} from '../../utils/getUserAbsenceReturnText';
import {getUserName} from '../../utils/getUserName';
import {handleApiError} from '../../utils/handleApiError';

interface UserAbsenceTooltipProps {
  absence: UserAvailabilitySummaryAbsenceResponseBody;
}

export function UserAbsenceTooltip(props: UserAbsenceTooltipProps) {
  const navigate = useNavigate();
  const formatDateTime = useDateTimeFormatter();
  const [getEmployeeLink] = useLazyGetEmployeeLinkQuery();

  const contactUser = props.absence.absenceContactUser;

  const handleContactUserClick = () => {
    if (!contactUser) {
      return;
    }

    getEmployeeLink({contextTarget: 'USER', contextId: contactUser.id})
      .unwrap()
      .then((links) => {
        const employeeId = links.find(isNotNil)?.employeeId;

        if (employeeId) {
          navigate(composePath(employeeRoutes.detail, {params: {id: employeeId}}));
        } else {
          showNotification.info(i18n.t('entity.userAbsence.notifications.substituteHasNoEmployee'));
        }
      })
      .catch(handleApiError);
  };

  return (
    <>
      {getUserAbsenceReturnText(props.absence.returnsAt, formatDateTime)}
      <Show when={isNotNilOrEmpty(props.absence.description)}>
        <br />
        {props.absence.description}
      </Show>
      <Show when={isNotNil(contactUser)}>
        <br />
        {i18n.t('entity.userAbsence.labels.absenceContactUser')}:{' '}
        <Link
          size="small"
          title={getUserName(contactUser) ?? contactUser?.email}
          onClick={handleContactUserClick}
        />
        <Show when={contactUser?.isAway || contactUser?.hasAbsence}>
          <br />
          {i18n.t('entity.userAbsence.tooltip.substituteAlsoAway')}
        </Show>
      </Show>
    </>
  );
}
